import { gql, useQuery } from "@apollo/client";
import useCheckDevice from "@repo/shopify/hooks/useCheckDevice";
import type { IMenuQuery } from "@repo/shopify/queries/others-gql";
import { menuQuery } from "@repo/shopify/queries/others-gql";
import type { IProductDetailQuery } from "@repo/shopify/queries/product-gql";
import { getTotalProductCount } from "@repo/shopify/utils/collections";
import classNames from "classnames";
import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

import Button from "@atoms/Button";
import Icon from "@atoms/Icon";
import Link from "@atoms/Link";
import Loading from "@atoms/Loading";

import Filter from "@molecules/Filter";
import ProductCard from "@molecules/ProductCard";

import { NOTIFICATIONS } from "@const/notifications";

import useCollections from "@hooks/useCollections";
import useHeaderScroll from "@hooks/useHeaderScroll";
import useLanguage from "@hooks/useLanguage";

import { useLayoutStore } from "@store/layout";

export default function Collections() {
  const { pathname } = useLocation();

  useHeaderScroll();

  const { collectionData, products, hasNextPage, loading, handleFetchMore, productCount } = useCollections();

  const [showFilter, setShowFilter] = useState<boolean>(false);

  const { domain, shopifyDomain } = useLanguage();

  const { openToast } = useLayoutStore();

  const { isPC } = useCheckDevice();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  useEffect(() => {
    if (collectionData?.collection.title !== "Daniel Johnston") return;

    document.body.setAttribute("class", "dark");
    return () => {
      document.body.setAttribute("class", "white");
    };
  }, [collectionData?.collection.title]);

  const { data: menuData } = useQuery<IMenuQuery>(gql(menuQuery.query), {
    variables: { handle: "navigation" },
    onError: (error) => {
      openToast(NOTIFICATIONS.ERROR_SERVER);
      throw new Error(error.message);
    },
  });

  useEffect(() => {
    setShowFilter(false);
  }, [pathname]);

  const totalProducts = useMemo(
    () => getTotalProductCount(collectionData?.collection.products.filters || []),
    [collectionData],
  );

  return (
    <>
      {!collectionData && loading ? (
        <Loading type="page" />
      ) : (
        <>
          {isPC ? (
            <section className="pc:col-span-2 pc:gap-5 pc:w-screen-col-span-2 sticky top-36 left-5 col-span-8 flex max-h-dvh gap-2 self-start overflow-y-auto pb-46">
              {showFilter ? (
                <Filter
                  filters={collectionData?.collection.products.filters || []}
                  onClickClose={() => {
                    setShowFilter(false);
                  }}
                />
              ) : (
                <div className="flex flex-col gap-10">
                  <Button
                    as="text"
                    className="justify-end"
                    onClick={() => {
                      setShowFilter(!showFilter);
                    }}
                  >
                    {`Filter & Sort (${totalProducts})`}
                  </Button>
                  <div className="flex flex-col gap-2">
                    {menuData?.menu?.items.map((item) => (
                      <React.Fragment key={item.id}>
                        {item.title !== "Archives" && (
                          <div className="tnt-gap-x pc:col-start-1 col-span-8 col-start-2 flex">
                            <Link
                              as="text"
                              className={classNames(
                                "pc:col-span-1 col-span-5 w-fit leading-none",
                                item.title === "Archives" && "pc:pt-2.5 pt-3.5",
                                !isPC && "text-base!",
                              )}
                              href={`/${item.url?.replace(domain, "").replace(shopifyDomain, "")}`}
                            >
                              {`${item.title}${item.title === "Archives" ? ":" : ""}`}
                            </Link>
                          </div>
                        )}

                        {item.title === "Archives" && (
                          <>
                            {isPC ? (
                              <>
                                <Link
                                  as="text"
                                  className="col-span-1 w-fit leading-none"
                                  href={`/${item.url?.replace(domain, "").replace(shopifyDomain, "")}`}
                                >
                                  Archives:
                                </Link>
                                {item.items.length ? (
                                  <div className="col-span-2 col-start-1 flex w-fit flex-col gap-2.5">
                                    <div className="ml-5 flex flex-col gap-2 dark:border-white">
                                      {item.items.map((subItem) => (
                                        <Link
                                          as="text"
                                          className={classNames("w-fit leading-none", !isPC && "text-base!")}
                                          href={`/${subItem.url?.replace(domain, "").replace(shopifyDomain, "")}`}
                                          key={subItem.id}
                                        >
                                          {subItem.title}
                                        </Link>
                                      ))}
                                    </div>
                                  </div>
                                ) : null}
                              </>
                            ) : (
                              <div className="col-start-2 flex gap-2">
                                <Link
                                  as="text"
                                  className="col-span-1 w-fit text-base! leading-none"
                                  href={`/${item.url?.replace(domain, "").replace(shopifyDomain, "")}`}
                                >
                                  Archives:
                                </Link>

                                {item.items.length ? (
                                  <div className="col-span-5 col-start-2 flex w-fit flex-col gap-2.5">
                                    <div className="flex flex-col gap-3.5 dark:border-white">
                                      {item.items.map((subItem) => (
                                        <Link
                                          as="text"
                                          className={classNames("w-fit leading-none", !isPC && "text-base!")}
                                          href={`/${subItem.url?.replace(domain, "").replace(shopifyDomain, "")}`}
                                          key={subItem.id}
                                        >
                                          {subItem.title}
                                        </Link>
                                      ))}
                                    </div>
                                  </div>
                                ) : null}
                              </div>
                            )}
                          </>
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              )}
            </section>
          ) : (
            <>
              {showFilter ? (
                <Filter
                  filters={collectionData?.collection.products.filters || []}
                  onClickClose={() => {
                    setShowFilter(false);
                  }}
                />
              ) : (
                <div
                  className="sticky top-0 z-20 col-span-8 flex w-full items-center justify-between gap-1 self-start bg-white py-2.5"
                  id="filter"
                >
                  <h1 className="text-title leading-none">{collectionData?.collection.title || ""}</h1>
                  <Button
                    as="wrapper"
                    className="h-fit"
                    onClick={() => {
                      setShowFilter(true);
                    }}
                  >
                    <Icon name="Filter" />
                  </Button>
                </div>
              )}
            </>
          )}

          <section className="pc:col-span-10 pc:grid-cols-5 pc:col-start-3 col-span-8 flex flex-col gap-2.5">
            {isPC ? <h1 className="text-title leading-none">{collectionData?.collection.title || ""}</h1> : null}
            <section className="tnt-gap-x pc:col-span-10 pc:grid-cols-5 pc:gap-y-20 relative col-span-8 grid grid-cols-2 gap-y-5">
              {products.map(({ node }: { node: IProductDetailQuery["product"] }, index) => (
                <ProductCard
                  color={node?.options.find((item) => item.name === "Color")?.values || []}
                  key={`${node?.id}_${index}`}
                  releaseDate={node?.release_date}
                  tags={node?.tags}
                  title={node?.title || ""}
                  variants={node?.variants}
                />
              ))}
            </section>

            {hasNextPage ? (
              <Button
                as="text"
                className="pc:col-span-12 col-span-8 my-30 flex size-full justify-center"
                loading={loading}
                onClick={() => {
                  void handleFetchMore();
                }}
              >
                {`Load More Products (${productCount} of ${totalProducts})`}
              </Button>
            ) : (
              <>
                {products.length ? (
                  <Button
                    as="text"
                    className="pc:col-span-12 col-span-8 my-30 flex size-full justify-center"
                    onClick={() => {
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                  >
                    Back to Top
                  </Button>
                ) : null}
              </>
            )}
          </section>
        </>
      )}
    </>
  );
}
